import { Box, Tooltip, Typography, styled } from '@mui/material';
import React, { useMemo } from 'react';
import { getBorderSegments, getMarkerColor, getMarkerIcon, getSortedTypesByCount, getValidMarkerType } from './MarkerUtils';

import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { ClusterMarkerProps } from './MarkerTypes';
import { LocationOn } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { BACKGROUND_COLORS, MAP_COLORS, RGBA_COLORS } from '@/common/presentation/constants/colors';

const StyledClusterMarker = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'borderSegments'
})<{ borderSegments: string }>(({ borderSegments }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  border: `8px solid transparent`,
  background: [
    `linear-gradient(135deg, ${MAP_COLORS.CLUSTER_GRADIENT_START}, ${MAP_COLORS.CLUSTER_GRADIENT_END}) padding-box`,
    `conic-gradient(${borderSegments}) border-box`
  ].join(', '),
  backgroundClip: 'padding-box, border-box',
  color: BACKGROUND_COLORS.WHITE,
  cursor: 'pointer',
  zIndex: 1,
  position: 'relative',
  boxShadow: MAP_COLORS.CLUSTER_SHADOW,
  '&:hover': {
    transform: 'scale(1.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: MAP_COLORS.CLUSTER_SHADOW_HOVER
  }
}));

const InnerCircle = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${MAP_COLORS.INNER_GRADIENT_START}, ${MAP_COLORS.INNER_GRADIENT_MID}, ${MAP_COLORS.INNER_GRADIENT_START})`,
  position: 'relative',
  boxSizing: 'border-box'
});

const MapPinIcon = styled(LocationOn)({
  fontSize: '28px !important',
  color: `${BACKGROUND_COLORS.WHITE} !important`,
  filter: `drop-shadow(0 2px 4px ${RGBA_COLORS.BLACK_38})`,
  opacity: 0.95
});

const OrbitingIcon = styled(Box)<{ angle: number; color: string }>(({ angle, color, theme }) => ({
  position: 'absolute',
  zIndex: -1,
  top: '50%',
  left: '50%',
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: `translate(-50%, -50%) rotate(${angle}deg) translate(15px) rotate(-${angle}deg)`,
  transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
  boxShadow: `0 3px 8px ${RGBA_COLORS.BLACK_25}, 0 1px 3px ${RGBA_COLORS.BLACK_15}`,
  // When parent cluster is hovered, expand the orbit radius
  '.cluster-marker:hover &': {
    transform: `translate(-50%, -50%) rotate(${angle}deg) translate(55px) rotate(-${angle}deg)`
  }
}));

// Enhanced Tooltip Component
const ClusterTooltip: React.FC<{ typeCounts: Record<string, number> }> = ({ typeCounts }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        minWidth: 160,
        backgroundColor: RGBA_COLORS.WHITE_95,
        borderRadius: '8px',
        boxShadow: MAP_COLORS.DETAILS_SHADOW,
        backdropFilter: 'blur(8px)'
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          color: MAP_COLORS.INNER_GRADIENT_START,
          borderBottom: '2px solid',
          borderBottomColor: 'info.main',
          pb: 0.5
        }}
      >
        Cluster Contents
      </Typography>
      {Object.entries(typeCounts).map(([type, count]) => {
        const validType = getValidMarkerType(type);
        const color = getMarkerColor(validType, theme);
        return (
          <Box
            key={type}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              p: 0.5,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: MAP_COLORS.FEATURE_HIGHLIGHT
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: color,
                  mr: 1.5,
                  boxShadow: MAP_COLORS.DETAILS_SHADOW,
                  border: `1px solid ${RGBA_COLORS.WHITE_90}`
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: MAP_COLORS.INNER_GRADIENT_MID,
                  fontWeight: 500
                }}
              >
                {type}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: MAP_COLORS.INNER_GRADIENT_START,
                backgroundColor: 'grey.100',
                px: 1,
                py: 0.25,
                borderRadius: '12px',
                fontSize: '0.75rem',
                minWidth: '24px',
                textAlign: 'center'
              }}
            >
              {count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const ClusterMarker: React.FC<ClusterMarkerProps> = ({ clusterId, position, features, onMarkerClick }) => {
  const theme = useTheme();

  // Calculate cluster data
  const clusterData = useMemo(() => {
    const types = features.map((f) => f.properties?.type).filter(Boolean);
    const typeCounts = types.reduce(
      (acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const sortedTypes = getSortedTypesByCount(types);
    const mainType = sortedTypes[0] || 'Jobsite';
    const borderSegments = getBorderSegments(types, theme);

    return { typeCounts, sortedTypes, mainType, borderSegments };
  }, [features, theme]);

  const handleClick = (e: google.maps.MapMouseEvent) => {
    if (e.domEvent.currentTarget instanceof google.maps.marker.AdvancedMarkerElement) {
      // Call the cluster click handler instead of marker click handler
      onMarkerClick(e.domEvent.currentTarget, clusterId);
    }
  };

  // Enhanced count display with better formatting
  const displayCount = features.length.toString();

  return (
    <AdvancedMarker position={position} onClick={handleClick}>
      <Tooltip
        title={<ClusterTooltip typeCounts={clusterData.typeCounts} />}
        placement="top"
        arrow
        PopperProps={{
          container: () => {
            const mapContainer = document.querySelector('#map-container');
            return mapContainer || document.body;
          },
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: 'transparent',
              padding: 0,
              boxShadow: 'none'
            },
            '& .MuiTooltip-arrow': {
              color: RGBA_COLORS.WHITE_95
            }
          }
        }}
      >
        <StyledClusterMarker borderSegments={clusterData.borderSegments} className="cluster-marker">
          <InnerCircle>
            <MapPinIcon />
            <Typography>{displayCount}</Typography>
          </InnerCircle>
          {/* Enhanced orbiting icons */}
          {clusterData.sortedTypes.map((type, index, arr) => {
            const IconComponent = getMarkerIcon(type);
            const total = arr.length;
            const angle = (index * 360) / total;
            const color = getMarkerColor(type, theme);
            return (
              <OrbitingIcon key={type} angle={angle} color={color}>
                <IconComponent sx={{ fontSize: 10, color: 'white' }} />
              </OrbitingIcon>
            );
          })}
        </StyledClusterMarker>
      </Tooltip>
    </AdvancedMarker>
  );
};

export default ClusterMarker;
